import {sendRefTelemetry} from '@github-ui/copilot-chat-helpers/custom-copilots-helpers'
import type {CopilotSpaceFreeTextResource, CopilotSpacesConfigPayload} from '../../types'
/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload --
   useAppPayload is deprecated. See
   https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import {testIdProps} from '@github-ui/test-id-props'
import {ScopedCommands} from '@github-ui/ui-commands'
import {Banner, Button, Dialog, FormControl, Textarea, TextInput} from '@primer/react'
import {useEffect, useState} from 'react'

import {useGetCopilotSpaceResourceSizes} from '../../hooks/use-get-copilot-space-resource-sizes'
import styles from './TextFileDialog.module.css'

interface TextFileDialogProps {
  resource?: CopilotSpaceFreeTextResource
  onClose: () => void
  onSave: (resource: CopilotSpaceFreeTextResource) => void
  sizePercentage?: number
  readOnly?: boolean
}

const FILE_NAME_MAX_LENGTH = 1_000

export function TextFileDialog({sizePercentage, onClose, onSave, resource, readOnly = false}: TextFileDialogProps) {
  const {
    copilotSpacesConfig: {maxContentSize},
  } = useAppPayload<CopilotSpacesConfigPayload>()
  const [name, setName] = useState(resource?.name || '')
  const [text, setText] = useState(resource?.text || '')
  const [isValidating, setIsValidating] = useState(false)
  const [validationError, setValidationError] = useState('')

  const [overSpaceLimit, setOverSpaceLimit] = useState(false)
  const originalContent = resource?.text ?? ''

  const [errorMessages, setErrorMessages] = useState<Record<string, string>>({})

  const {getCopilotSpaceResourceSizes} = useGetCopilotSpaceResourceSizes()

  const handleSubmit = async (e?: React.FormEvent): Promise<void> => {
    e?.preventDefault()
    // We want to stop the event from bubbling up to the copilot space form
    e?.stopPropagation()

    const nameMissing = !name.trim()
    const textMissing = !text.trim()
    if (nameMissing || textMissing) {
      setErrorMessages(prev => ({
        ...prev,
        'resources.name': nameMissing ? 'Name is required' : '',
        'resources.text': textMissing ? 'Content is required' : '',
      }))
      return
    }

    setIsValidating(true)
    setValidationError('')

    try {
      const newResource = {
        id: resource?.id || Date.now().toString(),
        databaseId: resource?.databaseId,
        type: 'free_text',
        name,
        text,
        markedForDestroy: false,
      } as CopilotSpaceFreeTextResource

      const validationResult = await getCopilotSpaceResourceSizes({
        resources: [newResource],
      })
      const res = {...newResource, sizePercentage: validationResult[newResource.id]}
      onSave(res)
      sendRefTelemetry([res], 'text-file-dialog')
      onClose()
    } catch {
      setValidationError('An unexpected error occurred while validating the text file')
    } finally {
      setIsValidating(false)
    }
  }

  return (
    <Dialog
      position={{narrow: 'fullscreen'}}
      title="Add text content"
      onClose={onClose}
      width="xlarge"
      height="large"
      renderBody={() => (
        <div className={styles.dialogBody}>
          {validationError && <Banner variant="critical" className="mb-2" title={validationError} />}
          <ScopedCommands className={styles.grow} commands={{'github:submit-form': () => void handleSubmit()}}>
            <form onSubmit={handleSubmit} className={styles.form}>
              <FormControl required>
                <FormControl.Label>Name</FormControl.Label>
                <TextInput
                  block
                  placeholder="Give the file a title"
                  value={name}
                  onChange={e => {
                    setName(e.target.value)
                    setErrorMessages(prev => ({...prev, 'resources.name': ''}))
                  }}
                  maxLength={FILE_NAME_MAX_LENGTH}
                  validationStatus={errorMessages['resources.name'] ? 'error' : undefined}
                  {...testIdProps('text-file-name-input')}
                  readOnly={readOnly}
                />
                {errorMessages['resources.name'] && (
                  <FormControl.Validation variant="error">{errorMessages['resources.name']}</FormControl.Validation>
                )}
              </FormControl>
              <FormControl required className={styles.grow}>
                <FormControl.Label>Content</FormControl.Label>
                <Textarea
                  block
                  name="content"
                  value={text}
                  placeholder="Enter content here"
                  resize="none"
                  className={styles.grow}
                  onChange={e => {
                    setText(e.target.value)
                    setErrorMessages(prev => ({...prev, 'resources.text': ''}))
                  }}
                  {...testIdProps('text-file-content-input')}
                  readOnly={readOnly}
                />
                {errorMessages['resources.text'] ? (
                  <FormControl.Validation variant="error">{errorMessages['resources.text']}</FormControl.Validation>
                ) : (
                  sizePercentage !== undefined && (
                    <CharacterCount
                      text={text}
                      percentage={sizePercentage}
                      resourceContent={originalContent}
                      setOverSpaceLimit={setOverSpaceLimit}
                      maxContentSize={maxContentSize}
                    />
                  )
                )}
              </FormControl>
            </form>
          </ScopedCommands>
        </div>
      )}
      renderFooter={() => (
        <div className={styles.footerContainer}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClickCapture={overSpaceLimit ? undefined : handleSubmit}
            loadingAnnouncement="Validating resource"
            inactive={overSpaceLimit}
            loading={isValidating}
            {...testIdProps('save-text-file-button')}
            disabled={readOnly}
          >
            Add
          </Button>
        </div>
      )}
    />
  )
}

function CharacterCount({
  text,
  percentage,
  resourceContent,
  setOverSpaceLimit,
  maxContentSize,
}: {
  text: string
  percentage: number
  resourceContent: string
  setOverSpaceLimit: React.Dispatch<React.SetStateAction<boolean>>
  maxContentSize: number
}) {
  const currentChars = (percentage / 100) * maxContentSize

  // if the user's added text is more than the resource content, the diff should be positive
  const diff =
    text.length > resourceContent.length
      ? Math.abs(text.length - resourceContent.length)
      : -Math.abs(text.length - resourceContent.length)

  const potentialChars = currentChars + diff
  // Use Math.floor to ensure the percentage does not prematurely show 100% when slightly below the threshold.
  const potentialPercentage = Math.floor((potentialChars / maxContentSize) * 100)
  const tooLong = potentialChars > maxContentSize

  useEffect(() => {
    setOverSpaceLimit(tooLong)
  }, [tooLong, setOverSpaceLimit])

  if (tooLong) {
    return (
      <FormControl.Validation variant="error">{`Total percent used: ${potentialPercentage}%`}</FormControl.Validation>
    )
  }

  return (
    // This is a direct child, it just doesn't know that because it is a fragment within a function call
    // eslint-disable-next-line primer-react/direct-slot-children
    <FormControl.Caption>{`Total percent used: ${potentialPercentage}%`}</FormControl.Caption>
  )
}
